<template>
  <div v-if="displayFields.length || tags.length" class="audit-group-metadata">
    <div v-if="displayFields.length" class="audit-group-fields">
      <div
        v-for="field in displayFields"
        :key="'field-' + field.id"
        class="audit-group-field">
        <strong class="audit-field-name">
          <AuditLogHighlight :text="field.name" :query="searchQuery"/>:
        </strong>

        <template v-if="editingFieldID === field.id">
          <div v-if="field.type === 'date'" class="audit-date-editor" @click.stop>
            <input
              ref="dateDay"
              v-model="draftDate.day"
              type="text"
              inputmode="numeric"
              maxlength="2"
              placeholder="DD"
              aria-label="Day"
              @input="sanitizeDatePart('day', 2)"
              @keydown.enter.exact.prevent="saveField(field)"
              @keydown.esc.prevent.stop="cancelEdit">
            <span>.</span>
            <input
              v-model="draftDate.month"
              type="text"
              inputmode="numeric"
              maxlength="2"
              placeholder="MM"
              aria-label="Month"
              @input="sanitizeDatePart('month', 2)"
              @keydown.enter.exact.prevent="saveField(field)"
              @keydown.esc.prevent.stop="cancelEdit">
            <span>.</span>
            <input
              v-model="draftDate.year"
              type="text"
              inputmode="numeric"
              maxlength="4"
              placeholder="YYYY"
              aria-label="Year"
              @input="sanitizeDatePart('year', 4)"
              @keydown.enter.exact.prevent="saveField(field)"
              @keydown.esc.prevent.stop="cancelEdit">
            <button type="button" class="btn btn-link audit-today-button" title="Use today's date" @click.stop="useToday">
              <i class="fa fa-calendar-check-o"></i>
            </button>
          </div>
          <textarea
            v-else
            v-audit-resize="'field-' + field.type"
            ref="fieldEditor"
            v-model="draftValue"
            class="form-control audit-field-editor"
            rows="2"
            maxlength="4000"
            :placeholder="field.type === 'usernames' ? 'Username1, Username2' : ''"
            @click.stop
            @keydown.enter.exact.prevent="saveField(field)"
            @keydown.esc.prevent.stop="cancelEdit">
          </textarea>
        </template>

        <template v-else-if="field.type === 'usernames'">
          <span v-if="field.users && field.users.length" class="audit-field-value audit-field-users">
            <router-link
              v-for="user in field.users"
              :key="'user-' + user.id"
              class="audit-field-user-link"
              :to="{ name: 'User', params: { username: user.username } }">
              <AuditLogHighlight :text="user.username" :query="searchQuery"/>
            </router-link>
          </span>
          <span v-else class="audit-field-empty">Not set</span>
        </template>

        <span v-else-if="field.value" class="audit-field-value">
          <template v-for="(segment, index) in valueSegments(field.value)">
            <a
              v-if="segment.url"
              :key="'field-url-' + field.id + '-' + index"
              href="#"
              @click.prevent.stop="confirmExternalLink(segment.text)">
              <AuditLogHighlight :text="segment.text" :query="searchQuery"/>
            </a>
            <AuditLogHighlight
              v-else
              :key="'field-text-' + field.id + '-' + index"
              :text="segment.text"
              :query="searchQuery"/>
          </template>
        </span>
        <span v-else class="audit-field-empty">Not set</span>

        <span v-if="editable && editingFieldID !== field.id" class="audit-field-actions">
          <button
            type="button"
            class="btn btn-link audit-field-edit"
            title="Edit field"
            @click.stop="startEdit(field)">
            <i class="fa fa-pencil"></i>
          </button>
          <button
            type="button"
            class="btn btn-link text-danger audit-field-delete"
            title="Delete field"
            @click.stop="confirmDeleteField(field)">
            <i class="fa fa-times"></i>
          </button>
        </span>
      </div>
    </div>

    <div v-if="tags.length" class="audit-group-tags">
      <span
        v-for="tag in tags"
        :key="'tag-' + tag.id"
        class="audit-group-tag"
        :class="tagClass(tag)">
        <span>{{ tag.label }}</span>
        <button
          v-if="editable"
          type="button"
          title="Remove tag"
          @click.stop="confirmDeleteTag(tag)">
          <i class="fa fa-times"></i>
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import iziToast from 'izitoast'
import AuditLogHighlight from './AuditLogHighlight.vue'

const urlPattern = /(https?:\/\/[^\s<>"']+)/gi

export default {
  name: 'AuditLogGroupMetadata',
  components: {
    AuditLogHighlight
  },
  props: {
    fields: {
      type: Array,
      default: () => []
    },
    tags: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: false
    },
    targetType: {
      type: String,
      default: 'group'
    },
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      editingFieldID: null,
      draftValue: '',
      draftDate: {
        day: '',
        month: '',
        year: ''
      }
    }
  },
  computed: {
    displayFields () {
      if (this.editable) {
        return this.fields
      }
      return this.fields.filter(field => {
        if (this.fieldMatchesSearch(field)) {
          return true
        }
        if (field.type === 'usernames') {
          return Array.isArray(field.users) && field.users.length > 0
        }
        return Boolean(String(field.value || '').trim())
      })
    }
  },
  methods: {
    fieldMatchesSearch (field) {
      const query = this.searchQuery.trim().toLocaleLowerCase()
      if (!query) {
        return false
      }
      const values = [field.name, field.value]
      if (field.type === 'usernames') {
        values.push(...(field.users || []).map(user => user.username))
      }
      return values.some(value => String(value || '').toLocaleLowerCase().includes(query))
    },
    startEdit (field) {
      this.editingFieldID = field.id
      this.draftValue = field.type === 'usernames'
        ? (field.users || []).map(user => user.username).join(', ')
        : (field.value || '')
      const dateParts = String(field.value || '').split('.')
      this.draftDate = {
        day: dateParts.length === 3 ? dateParts[0] : '',
        month: dateParts.length === 3 ? dateParts[1] : '',
        year: dateParts.length === 3 ? dateParts[2] : ''
      }
      this.$nextTick(() => {
        const input = field.type === 'date' ? this.$refs.dateDay : this.$refs.fieldEditor
        const element = Array.isArray(input) ? input[0] : input
        if (element) {
          element.focus()
          element.select()
        }
      })
    },
    cancelEdit () {
      this.editingFieldID = null
      this.draftValue = ''
    },
    sanitizeDatePart (part, maxLength) {
      this.$set(this.draftDate, part, String(this.draftDate[part] || '').replace(/\D/g, '').slice(0, maxLength))
    },
    useToday () {
      const today = new Date()
      this.draftDate = {
        day: String(today.getDate()).padStart(2, '0'),
        month: String(today.getMonth() + 1).padStart(2, '0'),
        year: String(today.getFullYear())
      }
    },
    normalizedDate () {
      const day = Number(this.draftDate.day)
      const month = Number(this.draftDate.month)
      const year = Number(this.draftDate.year)
      if (!day || !month || !year) {
        return ''
      }
      const date = new Date(year, month - 1, day)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null
      }
      return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${String(year).padStart(4, '0')}`
    },
    saveField (field) {
      let value = this.draftValue.trim()
      const payload = {
        field_id: Number(field.id),
        target_type: this.targetType
      }
      if (field.type === 'date') {
        value = this.normalizedDate()
        if (value === null) {
          iziToast.error({
            title: 'Invalid date',
            message: 'Enter a valid date.'
          })
          return
        }
      } else if (field.type === 'usernames') {
        payload.usernames = Array.from(new Set(value
          .split(',')
          .map(username => username.trim())
          .filter(Boolean)))
        if (payload.usernames.length > 10) {
          iziToast.error({
            title: 'Too many usernames',
            message: 'A Username/s field supports at most 10 users.'
          })
          return
        }
        value = ''
      }
      payload.value = value
      this.editingFieldID = null
      this.emit('updateAuditLogGroupField', payload)
    },
    valueSegments (value) {
      const parts = []
      let lastIndex = 0
      value.replace(urlPattern, (match, url, offset) => {
        if (offset > lastIndex) {
          parts.push({ text: value.slice(lastIndex, offset), url: false })
        }
        parts.push({ text: match, url: true })
        lastIndex = offset + match.length
        return match
      })
      if (lastIndex < value.length) {
        parts.push({ text: value.slice(lastIndex), url: false })
      }
      return parts
    },
    async confirmExternalLink (url) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'This link opens a page outside the tools and may be unsafe.',
        {
          title: 'Open external link?',
          okTitle: 'Open Link',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        window.open(url, '_blank', 'noopener')
      }
    },
    async confirmDeleteField (field) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Delete the field "${field.name}"?`,
        {
          title: 'Delete field?',
          okTitle: 'Delete Field',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        this.emit('deleteAuditLogGroupField', {
          field_id: Number(field.id),
          target_type: this.targetType
        })
      }
    },
    tagClass (tag) {
      if (tag.type === 'successfully_appealed' || tag.type === 'clean') {
        return 'audit-group-tag-success'
      }
      if (tag.type === 'appeal_denied' || tag.type === 'no_longer_welcome') {
        return 'audit-group-tag-danger'
      }
      return 'audit-group-tag-custom'
    },
    async confirmDeleteTag (tag) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Remove the tag "${tag.label}"?`,
        {
          title: 'Remove tag?',
          okTitle: 'Remove Tag',
          okVariant: 'danger',
          cancelTitle: 'Cancel',
          centered: true,
          headerBgVariant: 'dark',
          headerTextVariant: 'light',
          bodyBgVariant: 'dark',
          bodyTextVariant: 'light'
        }
      )
      if (confirmed) {
        this.emit('deleteAuditLogGroupTag', {
          tag_id: Number(tag.id),
          target_type: this.targetType
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.audit-group-metadata {
  margin-top: 4px;
}

.audit-group-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.audit-group-field {
  display: flex;
  min-width: 0;
  align-items: flex-start;
  gap: 5px;
  line-height: 1.55;
}

.audit-field-name {
  flex: 0 0 auto;
}

.audit-field-value {
  min-width: 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.audit-field-value a,
.audit-field-users a {
  color: #59dcc2;
}

.audit-field-users a {
  text-decoration: none;
}

.audit-field-users {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0;
}

.audit-field-user-link:not(:last-child)::after {
  content: ",\00a0";
}

.audit-field-empty {
  color: rgba(255, 255, 255, 0.42);
  font-style: italic;
}

.audit-field-edit,
.audit-field-delete,
.audit-today-button {
  min-width: 24px;
  padding: 0 4px;
}

.audit-field-actions {
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.audit-field-edit {
  color: rgba(255, 255, 255, 0.58);
}

.audit-field-editor {
  min-width: 280px;
  min-height: 58px;
}

.audit-date-editor {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.audit-date-editor input {
  width: 42px;
  height: 30px;
  padding: 3px 5px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  background: #20262c;
  color: #fff;
  text-align: center;
}

.audit-date-editor input:nth-of-type(3) {
  width: 62px;
}

.audit-today-button {
  color: #63c2de;
}

.audit-group-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
}

.audit-group-tag {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border: 1px solid;
  font-size: 0.76rem;
  font-weight: 600;
}

.audit-group-tag-success {
  border-color: #4dbd74;
  color: #79d69a;
}

.audit-group-tag-danger {
  border-color: #f86c6b;
  color: #ff9a99;
}

.audit-group-tag-custom {
  border-color: #20a8d8;
  color: #63c2de;
}

.audit-group-tag button {
  display: inline-flex;
  width: 0;
  height: 16px;
  padding: 0;
  margin-left: 0;
  border: 0;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: currentColor;
  opacity: 0;
  overflow: hidden;
  transition: width 0.12s ease, margin-left 0.12s ease, opacity 0.12s ease;
}

.audit-group-tag:hover button,
.audit-group-tag button:focus {
  width: 16px;
  margin-left: 5px;
  opacity: 1;
}
</style>
